import React, { FunctionComponent, useEffect, useState, useRef } from "react";
import { SiteLogoComponent, Questionnaire, ProgressStep, StepId } from "@iventis/components";
import { NewUserQuestionnaire } from "@iventis/domain-model/model/newUserQuestionnaire";
import { NewUserQuestionnaireEventType } from "@iventis/domain-model/model/newUserQuestionnaireEventType";
import { NewUserQuestionnaireExperience } from "@iventis/domain-model/model/newUserQuestionnaireExperience";
import { NewUserQuestionnaireReason } from "@iventis/domain-model/model/newUserQuestionnaireReason";
import { useIventisTranslate } from "@iventis/translations/use-iventis-translate";
import { Content } from "@iventis/translations/content/typed-content";
import Lottie from "react-lottie-player";
import { edgePaddingMinimum, ellipsisAnimation, fontSizes, media, sectionalMargin, zIndexes, styled } from "@iventis/styles";
import { Theme } from "@emotion/react";
import { API_KEY_QUERY_PARAM_KEY, SUPPORT_URL } from "@iventis/utilities";
import { libraryApi, permissionsApi } from "@iventis/api/src/api";
import { useHistory } from "@iventis/subscriptions";
import MapAnimation from "./assets/map-animation.json";
import { ErrorImage } from "./error_image";
// Controls how often we call the questionair route after it fails
const API_REQUEST_FREQUENCY_MS = 2000; // 2 seconds

const Onboarding: FunctionComponent = () => {
    const translate = useIventisTranslate();
    const [submitting, setSubmitting] = useState(false);

    const getUserFirstName = () => {
        const searchParams = new URLSearchParams(window.location.search);
        return searchParams.get("firstName");
    };

    const [loadingMessageStage, setLoadingMessageStage] = useState(0);
    const loadingMeessageStageRef = useRef(loadingMessageStage);

    const history = useHistory();

    const messages: { message: React.ReactNode; subtext: React.ReactNode; footerText: React.ReactNode; breakpointMS: number }[] = [
        {
            message: translate(Content.login2.questionnaire.ending_screen.initial_loading.message),
            subtext: translate(Content.login2.questionnaire.ending_screen.initial_loading.subtext),
            footerText: translate(Content.login2.questionnaire.ending_screen.initial_loading.footerText),
            breakpointMS: 0,
        },
        {
            message: translate(Content.login2.questionnaire.ending_screen.taking_longer.message),
            subtext: translate(Content.login2.questionnaire.ending_screen.taking_longer.subtext),
            footerText: translate(Content.login2.questionnaire.ending_screen.taking_longer.footerText),
            breakpointMS: 10000, // 10 seconds
        },
        {
            message: translate(Content.login2.questionnaire.ending_screen.error.message),
            subtext: translate(Content.login2.questionnaire.ending_screen.error.subtext),
            // eslint-disable-next-line no-template-curly-in-string
            footerText: (
                <>
                    {translate(Content.login2.questionnaire.ending_screen.error.footer_1)}{" "}
                    <StyledAnchor target="_blank" rel="noopener noreferrer" href={`${SUPPORT_URL}`}>
                        {translate(Content.login.footer.contact)}
                    </StyledAnchor>{" "}
                    {translate(Content.login2.questionnaire.ending_screen.error.footer_2)}
                </>
            ),
            breakpointMS: 60000, // 1 minute
        },
    ];

    useEffect(() => {
        if (submitting) {
            if (loadingMessageStage === messages.length - 1) return;
            setTimeout(() => {
                setLoadingMessageStage(loadingMessageStage + 1);
                loadingMeessageStageRef.current = loadingMessageStage + 1;
            }, messages[loadingMessageStage + 1].breakpointMS);
        }
    }, [submitting, loadingMessageStage]);

    const steps: ProgressStep[] = [
        {
            label: `${translate(Content.login2.questionnaire.step)} 1`,
            name: "step-1",
            id: StepId.reason,
            content: {
                question: translate(Content.login2.questionnaire.reason.question),
                options: [
                    { name: translate(Content.login2.questionnaire.reason.options.SitePlanForMyEvent), id: NewUserQuestionnaireReason.SitePlanForMyEvent, icon: "map" },
                    {
                        name: translate(Content.login2.questionnaire.reason.options.TeamPlanMoreEfficiently),
                        id: NewUserQuestionnaireReason.TeamPlanMoreEfficiently,
                        icon: "telescope",
                    },
                    {
                        name: translate(Content.login2.questionnaire.reason.options.PlanMultipleEventsPerProject),
                        id: NewUserQuestionnaireReason.PlanMultipleEventsPerProject,
                        icon: "magnifying-glass",
                    },
                    { name: translate(Content.login2.questionnaire.reason.options.JustCurious), id: NewUserQuestionnaireReason.JustCurious, icon: "thought-bubble" },
                ],
            },
        },
        {
            label: `${translate(Content.login2.questionnaire.step)} 2`,
            name: "step-2",
            id: StepId.eventType,
            content: {
                question: translate(Content.login2.questionnaire.eventType.question),
                options: [
                    { name: translate(Content.login2.questionnaire.eventType.options.SportsEvent), id: NewUserQuestionnaireEventType.SportsEvent, icon: "rugby-ball" },
                    { name: translate(Content.login2.questionnaire.eventType.options.RoadEvent), id: NewUserQuestionnaireEventType.RoadEvent, icon: "road" },
                    { name: translate(Content.login2.questionnaire.eventType.options.FestivalOrParade), id: NewUserQuestionnaireEventType.FestivalOrParade, icon: "party-horn" },
                    { name: translate(Content.login2.questionnaire.eventType.options.ExhibitionOrShow), id: NewUserQuestionnaireEventType.ExhibitionOrShow, icon: "panorama" },
                    { name: translate(Content.login2.questionnaire.eventType.options.MajorEvent), id: NewUserQuestionnaireEventType.MajorEvent, icon: "users" },
                    { name: translate(Content.login2.questionnaire.eventType.options.MultipleEvents), id: NewUserQuestionnaireEventType.MultipleEvents, icon: "calendar-range" },
                    { name: translate(Content.login2.questionnaire.eventType.options.Venue), id: NewUserQuestionnaireEventType.Venue, icon: "ring" },
                    { name: translate(Content.login2.questionnaire.eventType.options.SomethingElse), id: NewUserQuestionnaireEventType.SomethingElse, icon: "thumbtack" },
                ],
            },
        },
        {
            label: `${translate(Content.login2.questionnaire.step)} 3`,
            name: "step-3",
            id: StepId.experience,
            content: {
                question: translate(Content.login2.questionnaire.experience.question),
                options: [
                    { name: translate(Content.login2.questionnaire.experience.options.HaventUsedBefore), id: NewUserQuestionnaireExperience.HaventUsedBefore, icon: "1" },
                    { name: translate(Content.login2.questionnaire.experience.options.Powerpoint), id: NewUserQuestionnaireExperience.Powerpoint, icon: "2" },
                    { name: translate(Content.login2.questionnaire.experience.options.GoogleEarth), id: NewUserQuestionnaireExperience.GoogleEarth, icon: "3" },
                    { name: translate(Content.login2.questionnaire.experience.options.GisAndCadSoftware), id: NewUserQuestionnaireExperience.GisAndCadSoftware, icon: "4" },
                    { name: translate(Content.login2.questionnaire.experience.options.Iventis), id: NewUserQuestionnaireExperience.Iventis, icon: "5" },
                ],
            },
        },
        {
            label: `${translate(Content.login2.questionnaire.step)} 4`,
            name: "step-4",
            id: StepId.projectName,
            content: {
                question: translate(Content.login2.questionnaire.projectName.question),
                subLabel: translate(Content.login2.questionnaire.projectName.subLabel),
                options: [{ name: translate(Content.login2.questionnaire.projectName.options.projectName), id: StepId.projectName }],
            },
        },
    ];

    const stateSucceededRequests = useRef({ newUserQuestionnaire: false, projectApps: false });

    const onSubmit = async (data: NewUserQuestionnaire) => {
        try {
            if (!stateSucceededRequests.current.newUserQuestionnaire) {
                await permissionsApi.post("/new-user-questionnaire", data, { suppressToasts: true });
                stateSucceededRequests.current.newUserQuestionnaire = true;
            }
            if (!stateSucceededRequests.current.projectApps) {
                const res = await libraryApi.get<Node[]>("/applications", { suppressToasts: true });
                if (res.data.length === 0) {
                    throw new Error("No project applications");
                }
                stateSucceededRequests.current.projectApps = true;
            }

            // On success - redirect to root and let the router take us
            history.push("/");
            if (!window.location.href.includes(API_KEY_QUERY_PARAM_KEY)) {
                // If they are not an api user, we may need to reload the app as it's likely we have the wrong history
                window.location.reload();
            }
        } catch (err) {
            if (loadingMeessageStageRef.current === messages.length - 1) return;
            // It failed - Try the network request again after a given timeout
            setTimeout(() => onSubmit(data), API_REQUEST_FREQUENCY_MS);
        }
    };

    return (
        <>
            <TopMostContainer>
                <OnBoardingContainer>
                    <StyledLogoContainer>
                        <StyledLogo />
                    </StyledLogoContainer>
                    {!submitting && (
                        <Questionnaire
                            onSubmit={(data) => {
                                setSubmitting(true);
                                onSubmit(data);
                            }}
                            steps={steps}
                            user={{ firstName: getUserFirstName() }}
                        />
                    )}
                    {submitting && (
                        <CentralContainer>
                            {/* Loading animation */}
                            {loadingMessageStage !== messages.length - 1 && <StyledMapAnimation animationData={MapAnimation} loop play />}
                            {loadingMessageStage === messages.length - 1 && <StyledErrorImage />}
                            <ProgressText messages={messages}>{messages[loadingMessageStage].message}</ProgressText>
                            <ProgressSubText messages={messages} animateEllipsis={loadingMessageStage !== messages.length - 1}>
                                {messages[loadingMessageStage].subtext}
                            </ProgressSubText>
                            <ProgressFooterText>{messages[loadingMessageStage].footerText}</ProgressFooterText>
                        </CentralContainer>
                    )}
                </OnBoardingContainer>
            </TopMostContainer>
        </>
    );
};

const TopMostContainer = styled.div`
    height: 100vh;
    height: 100dvh;
    display: flex;
    justify-content: center;
    align-items: center;
`;

const OnBoardingContainer = styled.div`
    height: 100%;
    width: 100%;
    overflow: auto;
    display: flex;
    flex-direction: column;
`;

const StyledLogo = styled(SiteLogoComponent)`
    height: 75px;
    margin: 0;
    padding: 20px ${edgePaddingMinimum};
    max-width: none;

    ${media.medium} {
        padding: 40px ${edgePaddingMinimum};
    }
`;

const StyledLogoContainer = styled.div`
    width: 100%;
    display: flex;
    justify-content: center;
    align-items: center;
    text-align: center;
    position: sticky;
    top: 0;
    background-color: ${({ theme }: { theme: Theme }) => theme.typographyColors.blank};
    z-index: ${zIndexes.stickyComponents};
`;

const CentralContainer = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 40px;
    flex: 1 1;
    margin: ${sectionalMargin};
`;

const ProgressText = styled.p<{ messages: { message: React.ReactNode; subtext: React.ReactNode; footerText: React.ReactNode; breakpointMS: number }[] }>`
    color: ${({ theme }: { theme: Theme }) => theme.typographyColors.core};
    font-weight: 600;
    font-size: ${fontSizes.xxxxLarge};
    text-align: center;

    animation: ${({ messages }) =>
        messages
            .map<string>(
                (_, index) =>
                    `fadeOut ${messages[index + 1]?.breakpointMS ?? 0}ms linear ${messages.slice(0, index + 1).reduce<number>((cum, { breakpointMS }) => cum + breakpointMS, 0)}ms `
            )
            .join(", ")};

    @keyframes fadeOut {
        0% {
            opacity: 0;
        }
        15% {
            opacity: 1;
        }
        75% {
            opacity: 1;
        }
        100% {
            opacity: 0;
        }
    }

    ${media.extraSmall} {
        font-size: ${fontSizes.xLarge};
    }
`;

const ProgressSubText = styled.p<{
    animateEllipsis: boolean;
    messages: { message: React.ReactNode; subtext: React.ReactNode; footerText: React.ReactNode; breakpointMS: number }[];
}>`
    color: ${({ theme }: { theme: Theme }) => theme.typographyColors.primary};
    font-size: ${fontSizes.xxLarge};
    font-style: normal;
    font-weight: 600;
    text-align: center;

    animation: ${({ messages }) =>
        messages
            .map<string>(
                (_, index) =>
                    `fadeOut ${messages[index + 1]?.breakpointMS ?? 0}ms linear ${messages.slice(0, index + 1).reduce<number>((cum, { breakpointMS }) => cum + breakpointMS, 0)}ms `
            )
            .join(", ")};

    @keyframes fadeOut {
        0% {
            opacity: 0;
        }
        15% {
            opacity: 1;
        }
        75% {
            opacity: 1;
        }
        100% {
            opacity: 0;
        }
    }

    ${({ animateEllipsis }) => (animateEllipsis ? ellipsisAnimation : "")}

    ${media.extraSmall} {
        font-size: ${fontSizes.small};
    }
`;

const ProgressFooterText = styled.p`
    color: ${({ theme }: { theme: Theme }) => theme.typographyColors.core};
    font-size: ${fontSizes.xLarge};
    font-style: normal;
    font-weight: 600;
    line-height: normal;
    text-align: center;

    a {
        color: ${({ theme }: { theme: Theme }) => theme.typographyColors.primary};
        font-weight: 700;
    }

    ${media.extraSmall} {
        font-size: ${fontSizes.small};
    }
`;

const StyledMapAnimation = styled(Lottie)`
    width: 500px;
    height: 410px;

    ${media.extraSmall} {
        width: 351px;
        height: 253px;
    }
`;

const StyledErrorImage = styled(ErrorImage)`
    width: 500px;
    height: 410px;

    ${media.extraSmall} {
        width: 351px;
        height: 253px;
    }
`;

const StyledAnchor = styled.a`
    text-transform: lowercase;
`;

export default Onboarding;
